<template>
  <transition name="ai-slide-right">
    <aside v-if="open" class="ai-dock" role="complementary" aria-label="swiftChat">
      <div class="ai-dock__resize" @pointerdown="startResize"></div>
      <div v-if="isResizing" class="ai-dock__shield"></div>

      <header class="ai-drawer__header">
        <div class="ai-drawer__title">
          <i class="fa-solid fa-sparkles"></i>
          swiftChat
        </div>
        <button type="button" class="ai-icon-btn" title="Close" @click="close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </header>

      <div class="ai-drawer__body">
        <k-tabstrip :selected="selectedTab"
                    @select="onSelect"
                    tab-alignment="start"
                    :animation="false"
                    :tabs="AITabs"
                    class="ai-tabstrip">
          <template v-slot:UWGuides>
            <ai-frame :url="uwGuidesUrl" :azure-id="azureId" :user-name="userName" v-if="selectedTab === 0" />
          </template>
          <template v-slot:docsum>
            <ai-frame :url="docAnalysisUrl" :azure-id="azureId" :user-name="userName" v-if="selectedTab === 1" />
          </template>
          <template v-slot:pipeline>
            <ai-frame :url="pipelineUrl" :azure-id="azureId" :user-name="userName" v-if="selectedTab === 2" />
          </template>
          <template v-slot:product_finder>
            <ai-frame :url="productFinderUrl" :azure-id="azureId" :user-name="userName" v-if="selectedTab === 3" />
          </template>
        </k-tabstrip>
      </div>
    </aside>
  </transition>
</template>

<script>
  import { TabStrip } from '@progress/kendo-vue-layout';
  import aiFrame from './aiFrame.vue';
  import { CHAT_KIND_SWIFT, onChatOpen, offChatOpen } from '@/helpers/chatLauncher.js';

  // SwiftProcessingSPA builds these in whichever dashboard hosts AIFlyoutDrawer; the host-based
  // switch is copied verbatim so both apps point at the same environments.
  function aiBase() {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    return hostname.startsWith('dev.') || hostname === 'localhost'
      ? 'https://devai.goldstarfinancial.com'
      : 'https://ai.swiftapp.ai';
  }

  const MIN_WIDTH = 420;
  const MAX_WIDTH = 1200;
  const DEFAULT_WIDTH = 720;
  const WIDTH_KEY = 'swiftchat_dock_width';

  export default {
    name: 'SwiftChatDrawer',
    components: {
      'k-tabstrip': TabStrip,
      'ai-frame': aiFrame
    },
    data() {
      return {
        open: false,
        selectedTab: 0,
        isResizing: false,
        width: DEFAULT_WIDTH,
        azureId: localStorage.getItem('azure_id') || '',
        userName: localStorage.getItem('user_name') || '',
        // Same option set as SwiftProcessingSPA's LoanOfficerDashboard.
        AITabs: [
          { title: 'Search UW Guidelines', content: 'UWGuides' },
          { title: 'Document Analyzer', content: 'docsum' },
          { title: 'Pipeline Insights', content: 'pipeline' },
          { title: 'Product Finder', content: 'product_finder' }
        ]
      };
    },
    computed: {
      uwGuidesUrl() { return `${aiBase()}/chat/gpt/iframe/`; },
      docAnalysisUrl() { return `${aiBase()}/chat/summary`; },
      pipelineUrl() { return `${aiBase()}/chat/gpt/iframe/pipeline`; },
      productFinderUrl() { return `${aiBase()}/iframe/productFinder`; }
    },
    mounted() {
      onChatOpen(this.onChatOpen);
      const saved = parseInt(localStorage.getItem(WIDTH_KEY), 10);
      if (saved >= MIN_WIDTH && saved <= MAX_WIDTH) this.width = saved;
      this.applyWidth();
    },
    beforeUnmount() {
      offChatOpen(this.onChatOpen);
      window.removeEventListener('keydown', this.onKeyDown);
      this.stopResize();
    },
    methods: {
      onChatOpen(kind) {
        if (kind !== CHAT_KIND_SWIFT) return;
        this.open = true;
        window.addEventListener('keydown', this.onKeyDown);
      },
      close() {
        this.open = false;
        window.removeEventListener('keydown', this.onKeyDown);
      },
      onKeyDown(e) {
        if (e.key === 'Escape') this.close();
      },
      onSelect(e) {
        this.selectedTab = e.selected;
      },
      applyWidth() {
        this.$el && this.$el.style && this.$el.style.setProperty('--ai-dock-w', this.width + 'px');
        document.documentElement.style.setProperty('--ai-dock-w', this.width + 'px');
      },
      // Drag the left edge. The iframes swallow pointer events mid-drag, hence the shield.
      startResize(e) {
        this.isResizing = true;
        this._startX = e.clientX;
        this._startW = this.width;
        window.addEventListener('pointermove', this.onResize);
        window.addEventListener('pointerup', this.stopResize);
      },
      onResize(e) {
        if (!this.isResizing) return;
        const next = this._startW + (this._startX - e.clientX);
        this.width = Math.round(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, next)));
        this.applyWidth();
      },
      stopResize() {
        if (!this.isResizing) return;
        this.isResizing = false;
        window.removeEventListener('pointermove', this.onResize);
        window.removeEventListener('pointerup', this.stopResize);
        try { localStorage.setItem(WIDTH_KEY, String(this.width)); } catch (e) { /* private mode */ }
      }
    }
  };
</script>

<style scoped>
  .ai-dock {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: var(--ai-dock-w, 720px);
    max-width: 100vw;
    z-index: 1001;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-left: 1px solid rgba(15, 23, 42, .08);
    box-shadow: -18px 0 46px rgba(15, 23, 42, .16);
  }

  .ai-dock__resize {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 6px;
    cursor: ew-resize;
    z-index: 4;
  }

  .ai-dock__resize:hover {
    background: rgba(46, 107, 220, .18);
  }

  /* Covers the iframes so a drag that crosses them keeps reporting pointermove. */
  .ai-dock__shield {
    position: absolute;
    inset: 0;
    z-index: 3;
    cursor: ew-resize;
  }

  .ai-drawer__header {
    height: 64px;
    flex: 0 0 64px;
    padding: 0 14px 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    background: linear-gradient(#ffffff, #fbfcff);
  }

  .ai-drawer__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 900;
    font-size: 18px;
    color: #0f172a;
    letter-spacing: -0.2px;
  }

  .ai-icon-btn {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    border: 1px solid rgba(15, 23, 42, 0.10);
    background: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 10px rgba(15, 23, 42, 0.08);
    color: rgba(15, 23, 42, 0.72);
  }

  .ai-drawer__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  .ai-tabstrip {
    height: 100%;
    display: flex;
    flex-direction: column;
    border: none;
  }

  .ai-tabstrip :deep(.k-tabstrip-items) {
    background: transparent !important;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  }

  .ai-tabstrip :deep(.k-item) {
    border: none !important;
    background: transparent !important;
    font-weight: 800;
    color: rgba(15, 23, 42, 0.58);
  }

  .ai-tabstrip :deep(.k-item.k-active) {
    color: #0f172a;
    border-bottom: 3px solid #2e6bdc !important;
  }

  /* SwiftProcessingSPA pins this to calc(100vh - 64px) because its dock is full height.
     Flexing instead keeps it correct here too, and survives a shorter viewport. */
  .ai-tabstrip :deep(.k-content) {
    padding: 0 !important;
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
    background: #fff !important;
    border: none;
  }

  /* Kendo nests the panel two divs deep: .k-content > div > div[role=tabpanel] > .ai-frame.
     Every level needs a real box or the iframe collapses to its 300x150 intrinsic size,
     which is what left the AI stranded in the top-left corner. */
  .ai-tabstrip :deep(.k-content > div),
  .ai-tabstrip :deep(.k-content > div > [role='tabpanel']) {
    height: 100%;
    width: 100%;
    min-height: 0;
  }

  /* Defensive: if the tab animation is ever switched back on, Fade inserts two more
     wrappers between .k-content and the panel. Stretch those too. */
  .ai-tabstrip :deep(.k-animation-container),
  .ai-tabstrip :deep(.k-child-animation-container) {
    height: 100%;
    width: 100%;
  }

  .ai-slide-right-enter-active,
  .ai-slide-right-leave-active {
    transition: transform 0.22s cubic-bezier(.22, 1, .36, 1);
  }

  .ai-slide-right-enter-from,
  .ai-slide-right-leave-to {
    transform: translateX(100%);
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-slide-right-enter-active,
    .ai-slide-right-leave-active { transition: none; }
  }

  @media (max-width: 925px) {
    .ai-dock {
      width: 100vw;
      border-left: none;
    }
    .ai-dock__resize { display: none; }
  }
</style>
